import { useState } from 'react';
import { Loader2, LockKeyhole, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContentUltraPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatarTelefone } from '@/utils/formatters';
import { useLinhaSemFaturaAcoes, useLinhasSemFatura, usePlanosClaroMovel } from '@/hooks/useLinhaSemFatura';

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const rotulo = 'text-[10px] font-bold uppercase tracking-wider text-muted-foreground';

export function LinhaSemFaturaCard({ cnpj }: { cnpj: string }) {
  const { data: planos = [], isLoading: carregandoPlanos } = usePlanosClaroMovel(true);
  const { data: linhas = [] } = useLinhasSemFatura(cnpj, true);
  const { incluir, excluir } = useLinhaSemFaturaAcoes(cnpj);
  const [msisdn, setMsisdn] = useState('');
  const [plano, setPlano] = useState('');
  const [valor, setValor] = useState('');
  const [tm, setTm] = useState('');

  const salvar = () => {
    const numero = msisdn.replace(/\D/g, '');
    const valorNum = Number(valor.replace(/\./g, '').replace(',', '.'));
    const tmNum = Number(tm);
    if (numero.length !== 11) return toast.error('A linha precisa ter 11 dígitos com DDD.');
    if (!plano) return toast.error('Escolha o plano atual da linha.');
    if (!(valorNum > 0)) return toast.error('Informe o valor atual da linha.');
    if (tm === '' || !Number.isInteger(tmNum) || tmNum < 0) return toast.error('Informe o TM da linha em meses.');
    incluir.mutate({ msisdn: numero, plano, valor: valorNum, tm: tmNum }, {
      onSuccess: () => {
        toast.success('Linha incluída. Ela já entra no simulador e na proposta.');
        setMsisdn(''); setValor(''); setTm('');
      },
      onError: (e) => toast.error((e as Error).message),
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
          <LockKeyhole className="h-3.5 w-3.5 text-muted-foreground" />
          Linha sem fatura · só master
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Linha que ainda não aparece em fatura (ex.: TT recente). Entra no simulador e na proposta igual às da fatura.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.2fr_1.6fr_0.8fr_0.6fr_auto] md:items-end">
          <div className="space-y-1">
            <p className={rotulo}>Linha</p>
            <Input value={msisdn} onChange={(e) => setMsisdn(e.target.value)} placeholder="51999999999" inputMode="numeric" className="h-9 font-mono" />
          </div>
          <div className="space-y-1">
            <p className={rotulo}>Plano atual (Claro móvel)</p>
            <Select value={plano} onValueChange={setPlano} disabled={carregandoPlanos}>
              <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={carregandoPlanos ? 'Carregando…' : 'Escolha o plano'} /></SelectTrigger>
              <SelectContentUltraPriority>
                {planos.map((p) => (
                  <SelectItem key={p.plano} value={p.plano}>{p.plano}{Number(p.gb) > 0 ? ` · ${Number(p.gb)} GB` : ''}</SelectItem>
                ))}
              </SelectContentUltraPriority>
            </Select>
          </div>
          <div className="space-y-1">
            <p className={rotulo}>Valor R$</p>
            <Input value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0,00" inputMode="decimal" className="h-9" />
          </div>
          <div className="space-y-1">
            <p className={rotulo}>TM</p>
            <Input value={tm} onChange={(e) => setTm(e.target.value.replace(/\D/g, ''))} placeholder="meses" inputMode="numeric" className="h-9" />
          </div>
          <Button onClick={salvar} disabled={incluir.isPending} className="h-9 gap-1.5">
            {incluir.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Incluir
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          O plano sai da lista dos planos Claro móvel que o CRM já leu nas faturas. Se a linha aparecer na fatura nova, a linha da fatura entra no lugar da manual.
        </p>

        {linhas.length > 0 && (
          <div className="rounded-md border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">Linha</th>
                  <th className="px-3 py-2 font-semibold">Plano</th>
                  <th className="px-3 py-2 font-semibold">Valor</th>
                  <th className="px-3 py-2 font-semibold">Origem</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.msisdn} className="border-t border-border">
                    <td className="px-3 py-2 font-mono">{formatarTelefone(l.msisdn)}</td>
                    <td className="px-3 py-2">{l.plano}</td>
                    <td className="px-3 py-2">{moeda.format(Number(l.valor || 0))}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.na_fatura ? 'Já veio na fatura · vale a da fatura' : l.rotulo}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        aria-label="Excluir linha"
                        disabled={excluir.isPending}
                        onClick={() => excluir.mutate(l.msisdn, { onError: (e) => toast.error((e as Error).message) })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
